/* eslint-disable react-refresh/only-export-components */
import { ApolloProvider as ApolloClientProvider, useQuery } from "@apollo/client/react";
import { fetchAuthSession, getCurrentUser, signInWithRedirect } from "aws-amplify/auth";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AuthenticationLoading, NoRoleAssigned } from "../../components/auth";
import { configureAmplify } from "../../configs/amplify";
import { WhoAmIDocument } from "../../services/__generated__/graphql";
import { AuthenticationService } from "../../services/authentication";
import { apolloClient } from "../../services/backend_gateway/apollo-client";
import { AuthContext } from "./context";

// Ensure Amplify is configured (configuration happens in amplify.ts on module load)
configureAmplify();

export { AuthContext } from "./context";

const AuthCheck = ({
  loading,
  authenticated,
}: {
  loading: ReactNode;
  authenticated: ReactNode;
}): React.JSX.Element => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [hasIdToken, setHasIdToken] = useState<boolean>(false);
  const [idToken, setIdToken] = useState<string | undefined>(undefined);

  const getToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      const accessToken = session.tokens?.accessToken;

      if (idToken) {
        const idTokenString = idToken.toString();
        // Only update if token actually changed
        setIdToken((prev) => (prev !== idTokenString ? idTokenString : prev));
        AuthenticationService.setIdToken(idTokenString);

        // Set access token for API Gateway authentication
        if (accessToken) {
          AuthenticationService.setAccessToken(accessToken.toString());
        }

        setHasIdToken((prev) => (prev !== true ? true : prev));
      } else {
        setHasIdToken((prev) => (prev !== false ? false : prev));
      }
      // Clear error on success
      setError((prev) => (prev !== null ? null : prev));
    } catch (e) {
      console.error("Error getting token:", e);
      const error = e as Error;
      setError((prev) => {
        // Only update if error actually changed
        if (prev?.message !== error.message) return error;
        return prev;
      });
      setHasIdToken((prev) => (prev !== false ? false : prev));
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading((prev) => (prev !== true ? true : prev));
        setError((prev) => (prev !== null ? null : prev));
        performance.mark("auth-start");

        // PERF: Run getCurrentUser and getToken in parallel for ~500-800ms improvement
        // Both are independent network calls that don't depend on each other
        await Promise.all([getCurrentUser(), getToken()]);
        // If we get here, user is authenticated (getCurrentUser succeeded)
        setIsAuthenticated((prev) => (prev !== true ? true : prev));

        performance.mark("auth-complete");
        performance.measure("auth-duration", "auth-start", "auth-complete");
        console.log(
          "[PERF] Auth completed:",
          performance.getEntriesByName("auth-duration")[0]?.duration.toFixed(0) + "ms"
        );
      } catch (error) {
        console.log("User not authenticated:", error);
        setIsAuthenticated((prev) => (prev !== false ? false : prev));
        setError((prev) => (prev !== null ? null : prev));
      } finally {
        setIsLoading((prev) => (prev !== false ? false : prev));
      }
    };
    checkAuth();
  }, [getToken]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !redirecting) {
      setRedirecting(true);
      signInWithRedirect().catch(() => {
        setRedirecting(false);
      });
    }
  }, [isLoading, isAuthenticated, redirecting]);

  // Set up token refresh callback - memoized to prevent recreation
  const handleTokenRefresh = useCallback(async (newToken: string) => {
    // Only update if token actually changed
    setIdToken((prev) => (prev !== newToken ? newToken : prev));
    AuthenticationService.setIdToken(newToken);

    // Also refresh access token when ID token is refreshed
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken;
      if (accessToken) {
        AuthenticationService.setAccessToken(accessToken.toString());
      }
    } catch (error) {
      console.warn("Failed to refresh access token:", error);
    }
  }, []);

  useEffect(() => {
    AuthenticationService.setOnTokenRefresh(handleTokenRefresh);

    return () => {
      AuthenticationService.setOnTokenRefresh(() => {});
    };
  }, [handleTokenRefresh]);

  // Memoize base context value (without user - user will be added by inner component)
  const baseContextValue = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      error,
      idToken,
      hasIdToken,
      user: null, // Will be set by AuthContextWithUser component
    }),
    [isLoading, isAuthenticated, error, idToken, hasIdToken]
  );

  if (isLoading) return loading as React.JSX.Element;
  if (error) return <div>Error: {error.message}</div>;
  if (!isAuthenticated) return <div>Redirecting to sign in...</div>;
  if (!hasIdToken) return loading as React.JSX.Element;

  return (
    <AuthContext.Provider value={baseContextValue}>
      <ApolloClientProvider client={apolloClient}>
        <AuthContextWithUser>{authenticated}</AuthContextWithUser>
      </ApolloClientProvider>
    </AuthContext.Provider>
  );
};

/** Defer WhoAmI by this many ms on cold load (set to 0 to disable). */
const DEFER_WHOAMI_MS: number = 300;

// Component that fetches user data and updates context (must be inside ApolloProvider)
const AuthContextWithUser = ({ children }: { children: ReactNode }) => {
  const baseContext = useContext(AuthContext);
  if (!baseContext) {
    throw new Error("AuthContextWithUser must be used within AuthContext.Provider");
  }

  // CHO-617 Phase 8 (QW2): defer WhoAmI on cold load so the critical
  // CreateMyViewTransformVT requests get the connection slot first (the cold HAR
  // shows the backendgateway fanout contending with the VT POSTs). Mirrors
  // dashmart's DEFER_WHOAMI_MS. Set to 0 to disable (fire immediately).
  const [whoAmIEnabled, setWhoAmIEnabled] = useState(DEFER_WHOAMI_MS === 0);
  useEffect(() => {
    if (DEFER_WHOAMI_MS === 0) return;
    const timer = setTimeout(() => setWhoAmIEnabled(true), DEFER_WHOAMI_MS);
    return () => clearTimeout(timer);
  }, []);

  // Fetch user information via WhoAmI query (only when authenticated)
  const { data: userData } = useQuery(WhoAmIDocument, {
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: !whoAmIEnabled,
  });

  // Normalize user data to match our interface (convert undefined to null)
  // Depend on actual primitive values to prevent unnecessary recalculations
  // when Apollo returns new object references with same data
  const whoAmI = userData?.whoAmI;
  const userId = whoAmI?.id ?? null;
  const userName = whoAmI?.name ?? null;
  const userEmail = whoAmI?.email ?? null;
  const userPicture = whoAmI?.picture ?? null;
  const userPhoneNumber = whoAmI?.phoneNumber ?? null;
  const userRole = whoAmI?.role ?? null;
  const userCustomerId = whoAmI?.customerId ?? null;
  // Whether this caller may use Nucleus, as the GATEWAY decides it. Read, never
  // derived: the dock used to test `role === "ROOT"` here and in the sidebar,
  // which is the same rule written a third and fourth time and free to drift
  // from the guard that actually refuses (NUC-296). Defaults to false while
  // WhoAmI is still loading, so a surface never opens before the answer.
  const userNucleusAdmitted = whoAmI?.nucleusAdmitted ?? false;

  const user = useMemo(() => {
    if (!whoAmI) return null;
    return {
      id: userId,
      name: userName,
      email: userEmail,
      picture: userPicture,
      phoneNumber: userPhoneNumber,
      role: userRole,
      customerId: userCustomerId,
      nucleusAdmitted: userNucleusAdmitted,
    };
  }, [whoAmI, userId, userName, userEmail, userPicture, userPhoneNumber, userRole, userCustomerId, userNucleusAdmitted]);

  // Memoize context value with user info
  const contextValue = useMemo(
    () => ({
      ...baseContext,
      user,
    }),
    [baseContext, user]
  );

  // A signed-in user with NO role at all: authentication succeeded, but every role has
  // been revoked, so the gateway writes an empty `custom:role` and an empty RBAC set.
  // There is deliberately no fallback role — emptying a set used to collapse to ADMIN,
  // promoting the very people an operator had just restricted. Render the explanation
  // instead of the app, whose every query would 403 and read as breakage.
  //
  // Gated on `whoAmI` being present so the still-loading case (no data yet, or WhoAmI
  // deferred by DEFER_WHOAMI_MS) is never mistaken for rolelessness.
  const hasNoRole = whoAmI != null && !userRole;

  return (
    <AuthContext.Provider value={contextValue}>
      {hasNoRole ? <NoRoleAssigned /> : children}
    </AuthContext.Provider>
  );
};

const AuthContent = ({ children }: { children: ReactNode }) => {
  return <AuthCheck loading={<AuthenticationLoading />} authenticated={children} />;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  return <AuthContent>{children}</AuthContent>;
};

// Re-export types for convenience
export type { AuthContextType } from "./types";
